<app-layout>
    <h1 class="mb-8 font-bold text-3xl">
        <inertia-link class="text-indigo-400 hover:text-indigo-600" :href="route('admin.markets')">Markets</inertia-link>
        - {{ subTitle }}
    </h1>
    <trashed-message v-if="isEdit && market.deleted_at" class="mb-6" @restore="restore">
        This market has been deleted.
    </trashed-message>
    <div class="bg-white rounded shadow w-full lg:w-1/2">
        <form @submit.prevent="submit">
            <div class="p-8 -mr-6 -mb-8 flex flex-wrap">
                <div class="pr-6 pb-8 w-full">
                    <div class="font-weight-bold text-xl border-b mb-2 pb-4 font-medium">General Information</div>
                </div>
                <div class="pr-6 pb-8 w-full lg:w-1/2">
                    <label class="form-label d-block">Base Currency:</label>
                    <t-rich-select
                        :options="currencies"
                        v-model="form.base_currency_id"
                        placeholder="Select currency"
                        value-attribute="id"
                        text-attribute="name"
                    >
                        <template
                            slot="label"
                            slot-scope="{ className, option, query }"
                        >
                            <div class="flex">
                                <div class="flex flex-col">
                                    <span>{{ option.text }}</span>
                                </div>
                            </div>
                        </template>
                    </t-rich-select>
                    <div v-if="errors.base_currency_id" class="form-error text-red-400">{{ errors.base_currency_id }}</div>
                </div>

                <div class="pr-6 pb-8 w-full lg:w-1/2">
                    <label class="form-label d-block">Quote Currency:</label>
                    <t-rich-select
                        v-model="form.quote_currency_id"
                        :options="currencies"
                        placeholder="Select currency"
                        value-attribute="id"
                        text-attribute="name"
                    >
                        <template
                            slot="label"
                            slot-scope="{ className, option, query }"
                        >
                            <div class="flex">
                                <div class="flex flex-col">
                                    <span>{{ option.text }}</span>
                                </div>
                            </div>
                        </template>
                    </t-rich-select>
                    <div v-if="errors.quote_currency_id" class="form-error text-red-400">{{ errors.quote_currency_id }}</div>
                </div>

                <text-input v-model="form.name" :error="errors.name" class="pr-6 pb-8 w-full lg:w-1/2" label="Name" />

                <text-input v-model="form.last" :error="errors.last" class="pr-6 pb-8 w-full lg:w-1/2" label="Market Price" />

                <div class="pr-6 pb-8 w-full">
                    <div class="font-weight-bold text-xl border-b mt-5 mb-2 pb-4 font-medium">Trading Rules</div>
                </div>

                <text-input v-model="form.base_precision" :error="errors.base_precision" class="pr-6 pb-8 w-full lg:w-1/2" label="Base Currency Precision" />
                <text-input v-model="form.quote_precision" :error="errors.quote_precision" class="pr-6 pb-8 w-full lg:w-1/2" label="Quote Currency Precision" />

                <text-input v-model="form.min_trade_size" :error="errors.min_trade_size" class="pr-6 pb-8 w-full lg:w-1/2" label="Min Trade Size (Base Currency)" />
                <text-input v-model="form.max_trade_size" :error="errors.max_trade_size" class="pr-6 pb-8 w-full lg:w-1/2" label="Max Trade Size (Base Currency)" />

                <text-input v-model="form.min_trade_value" :error="errors.min_trade_value" class="pr-6 pb-8 w-full lg:w-1/2" label="Min Trade Value (Quote Currency)" />
                <text-input v-model="form.max_trade_value" :error="errors.max_trade_value" class="pr-6 pb-8 w-full lg:w-1/2" label="Max Trade Value (Quote Currency)" />

                <text-input v-model="form.base_ticker_size" :error="errors.base_ticker_size" class="pr-6 pb-8 w-full lg:w-1/2" label="Ticker Size (Base Currency)" />
                <text-input v-model="form.quote_ticker_size" :error="errors.quote_ticker_size" class="pr-6 pb-8 w-full lg:w-1/2" label="Ticker Size (Quote Currency)" />

                <text-input v-model="form.discount" :error="errors.discount" class="pr-6 pb-8 w-full lg:w-1/2" label="Discount (in % 0:100)" />


                <div class="pr-6 pb-8 w-full">
                    <div class="font-weight-bold text-xl border-b mt-5 mb-2 pb-4 font-medium">Market Status</div>
                </div>

                <div class="pr-6 pb-8 w-full lg:w-1/2">
                    <label class="form-label block mr-2 mb-3">Market Visibility</label>
                    <t-toggle v-model="form.status" />
                </div>

                <div class="pr-6 pb-8 w-full lg:w-1/2">
                    <label class="form-label block mr-2 mb-3">Market Trade Allowed</label>
                    <t-toggle v-model="form.trade_status" />
                </div>

                <div class="pr-6 pb-8 w-full lg:w-1/2">
                    <label class="form-label block mr-2 mb-3">Market Buy Orders Allowed</label>
                    <t-toggle v-model="form.buy_order_status" />
                </div>

                <div class="pr-6 pb-8 w-full lg:w-1/2">
                    <label class="form-label block mr-2 mb-3">Market Sell Orders Allowed</label>
                    <t-toggle v-model="form.sell_order_status" />
                </div>

                <div class="pr-6 pb-8 w-full lg:w-1/2">
                    <label class="form-label block mr-2 mb-3">Market Cancel Orders Allowed</label>
                    <t-toggle v-model="form.cancel_order_status" />
                </div>

                <div class="pr-6 pb-8 w-full lg:w-1/2">
                    <label class="form-label block mr-2 mb-3">Display External Tradingview Chart</label>
                    <t-toggle v-model="form.is_tradingview" />
                </div>

                <div class="pr-6 pb-8 w-full lg:w-1/2">
                    <label class="form-label block mr-2 mb-3">Has Futures?</label>
                    <t-toggle v-model="form.has_futures" />
                </div>
            </div>
            <div class="px-8 py-4 bg-gray-100 border-t border-gray-200 flex items-center">
                <button v-if="isEdit && !market.deleted_at" class="focus:outline-none text-red-600 hover:underline" tabindex="-1" type="button" @click="destroy">Delete Market</button>

                <loading-button :loading="sending" class="btn-indigo ml-auto" type="submit">
                    <svg v-show="sending" class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {{ actionButtonTitle }}
                </loading-button>
            </div>
        </form>
    </div>
</app-layout>
